/**
 * Colour ramps for Ember sprites.
 *
 * A material is a five-step ramp plus the outline drawn where it meets the background. The shader
 * picks a step from lighting; it never invents colours, which is what keeps the output reading as
 * pixel art rather than a render. Skin ramps are sampled from the LPC bodies so Ember bodies sit
 * beside the existing art; everything else is derived from one base colour by `rampFrom`.
 */
import { bindContent } from "../content/catalog"
import { noteEmberContent } from "./model"
export interface Material {
    /** Brightest to darkest. `line` marks creases and limbs crossing the body. */
    light: number
    base: number
    shade: number
    deep: number
    line: number
    outline: number
}

const hex = (s: string): number => parseInt(s.replace("#", ""), 16)

const material = (light: string, base: string, shade: string, deep: string, line: string, outline: string): Material => ({
    light: hex(light),
    base: hex(base),
    shade: hex(shade),
    deep: hex(deep),
    line: hex(line),
    outline: hex(outline),
})

/**
 * The colours Ember draws with come from content (`content/game/sprites/ember/palettes.json`): skin
 * ramps by skin set (a style draws skin in one: `lpc` sampled off the LPC sheets, `painted` Ember's
 * own), bone, eye colours, cloth dyes and hair colours. These records are filled in place when the
 * content is bound -- the same objects, whoever imported them -- and follow it when it changes.
 */
export const SKIN: Record<string, Material> = {}
export const PAINTED_SKIN: Record<string, Material> = {}
/** Skin sets by name (palettes.json `skins`): `lpc` is SKIN, `painted` is PAINTED_SKIN. */
export const SKIN_SETS: Record<string, Record<string, Material>> = { lpc: SKIN, painted: PAINTED_SKIN }

/** Eye colours: glint, sclera, iris, pupil off the LPC sheets; Ember's warm catch-light and pupil; a wolf's glow. */
export const EYE: Record<string, number> = {}

/** A skeleton's bone by the game's skeleton body colours (palettes.json ramps.bones). */
export const BONES: Record<string, Material> = {}

/** Cloth and leather dyes an Ember humanoid's outfit may name. */
export const DYES: Record<string, string> = {}

export const HAIR: Record<string, string> = {}

/** A ramp as content writes it: [light, base, shade, deep, line, outline]. */
export type RampSource = [string, string, string, string, string, string]
export interface EmberPaletteFile {
    version: 1
    about?: string
    ramps: Record<string, { about?: string; tones: Record<string, RampSource> }>
    skins: Record<string, string[]>
    eye: { about?: string; colors: Record<string, string> }
    dyes: { about?: string; colors: Record<string, string> }
    hair: { about?: string; colors: Record<string, string> }
}

const fill = <T>(target: Record<string, T>, next: Record<string, T>) => {
    for (const k of Object.keys(target)) delete target[k]
    Object.assign(target, next)
}
const rampOf = (r: RampSource): Material => material(...r)
const tonesOf = (group: { tones: Record<string, RampSource> } | undefined): Record<string, Material> =>
    Object.fromEntries(Object.entries(group?.tones ?? {}).map(([name, r]) => [name, rampOf(r)]))

let bound: EmberPaletteFile | undefined
let previewTones: Record<string, RampSource> | undefined

/** Fill the palettes from content's palettes.json (bound below; tools that hold a catalog call it themselves). */
export const registerPalettes = (file: EmberPaletteFile | undefined): void => {
    bound = file
    applyPalettes()
    noteEmberContent("palettes", file)
}

/**
 * Skin tones a tool is previewing, added to every skin set over the game's: a draft creature's own
 * skin, drawn before it is saved. Undefined clears them.
 */
export const setPreviewSkinTones = (tones: Record<string, RampSource> | undefined): void => {
    previewTones = tones && Object.keys(tones).length ? tones : undefined
    applyPalettes()
    noteEmberContent("skin-preview", previewTones)
}

const applyPalettes = (): void => {
    const file = bound
    const groups = Object.fromEntries(Object.entries(file?.ramps ?? {}).map(([name, group]) => [name, tonesOf(group)]))
    for (const name of Object.keys(SKIN_SETS)) if (!file?.skins?.[name] && name !== "lpc" && name !== "painted") delete SKIN_SETS[name]
    for (const [name, list] of Object.entries(file?.skins ?? {})) fill(SKIN_SETS[name] ??= {}, Object.assign({}, ...list.map(group => groups[group] ?? {})))
    for (const name of ["lpc", "painted"]) if (!file?.skins?.[name]) fill(SKIN_SETS[name], {})
    fill(BONES, groups.bones ?? {})
    fill(EYE, Object.fromEntries(Object.entries(file?.eye?.colors ?? {}).map(([name, c]) => [name, hex(c)])))
    fill(DYES, { ...(file?.dyes?.colors ?? {}) })
    fill(HAIR, { ...(file?.hair?.colors ?? {}) })
    if (previewTones) for (const set of Object.values(SKIN_SETS)) for (const [name, r] of Object.entries(previewTones)) set[name] = rampOf(r)
}

/** A skin tone's ramp from one base colour, as content writes it: shadows toward violet, highlights warm (rampFrom). */
export const rampSourceFrom = (base: string): RampSource => {
    const m = rampFrom(base)
    return [m.light, m.base, m.shade, m.deep, m.line, m.outline].map(c => `#${c.toString(16).padStart(6, "0")}`) as RampSource
}

const rgb = (c: number): [number, number, number] => [(c >> 16) & 255, (c >> 8) & 255, c & 255]
const pack = ([r, g, b]: [number, number, number]): number =>
    (Math.round(Math.max(0, Math.min(255, r))) << 16) |
    (Math.round(Math.max(0, Math.min(255, g))) << 8) |
    Math.round(Math.max(0, Math.min(255, b)))

const toHsl = (c: number): [number, number, number] => {
    const [r, g, b] = rgb(c).map(v => v / 255) as [number, number, number]
    const max = Math.max(r, g, b), min = Math.min(r, g, b)
    const l = (max + min) / 2
    if (max === min) return [0, 0, l]
    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    return [h * 60, s, l]
}

const fromHsl = (h: number, s: number, l: number): number => {
    const hue = ((h % 360) + 360) % 360
    const c = (1 - Math.abs(2 * l - 1)) * s
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
    const m = l - c / 2
    const [r, g, b] =
        hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x] : hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x]
    return pack([(r + m) * 255, (g + m) * 255, (b + m) * 255])
}

/**
 * A pixel-art ramp from one base colour: shadows drift toward violet and lose lightness, highlights
 * drift toward warm yellow. The same hue shift LPC's cloth uses, so dyed garments read as painted.
 */
export const rampFrom = (base: string | number, outline = "#281820"): Material => {
    const [h, s, l] = toHsl(typeof base === "string" ? hex(base) : base)
    const toward = (target: number, amount: number) => {
        let d = target - h
        if (d > 180) d -= 360
        if (d < -180) d += 360
        return h + d * amount
    }
    return {
        light: fromHsl(toward(55, 0.15), Math.min(1, s * 0.95), Math.min(0.93, l + 0.13)),
        base: fromHsl(h, s, l),
        shade: fromHsl(toward(265, 0.12), Math.min(1, s * 1.02), Math.max(0.05, l - 0.12)),
        deep: fromHsl(toward(265, 0.22), Math.min(1, s * 1.05), Math.max(0.04, l - 0.22)),
        line: fromHsl(toward(265, 0.3), Math.min(1, s * 0.9), Math.max(0.03, l - 0.32)),
        outline: hex(outline),
    }
}

/**
 * Polished metal: the same hue with a near-white glint and a deep shadow, more contrast than cloth
 * or leather, so a breastplate reads as metal at a few pixels.
 */
export const metalFrom = (base: string, outline = "#281820"): Material => {
    const [h, s, l] = toHsl(hex(base))
    return {
        light: fromHsl(h, s * 0.6, Math.min(0.94, l + 0.27)),
        base: fromHsl(h, s, l),
        shade: fromHsl(h, Math.min(1, s * 1.05), Math.max(0.05, l - 0.17)),
        deep: fromHsl(h, Math.min(1, s * 1.1), Math.max(0.04, l - 0.31)),
        line: fromHsl(h, Math.min(1, s * 1.1), Math.max(0.03, l - 0.4)),
        outline: hex(outline),
    }
}

export const dye = (name: string): Material => rampFrom(DYES[name] ?? DYES.gray)
export const hairDye = (name: string): Material => rampFrom(HAIR[name] ?? HAIR.brown)

bindContent(catalog => (catalog.source as any).emberPalettes, registerPalettes)
